import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CommunicationUserIdentifier } from "@azure/communication-common";
import { CustomCallView, CustomCallViewHandle } from "../azure/views/CustomCallView";
import { getPatientUserTokenFun, addUserToRoomFun } from "../azure/action/action";
import { fetchTokenData } from "../../Common/axios_instance/axios_instance";
import { callManager } from "../azure/call-manager";
import style from "./PatientJoin.module.css";

type JoinState = "loading" | "ready" | "ended" | "error";

export const PatientJoin: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [joinState, setJoinState] = useState<JoinState>("loading");
  const [loadingText, setLoadingText] = useState("Connecting you to your doctor...");
  const [patientName, setPatientName] = useState("Patient");
  const [errorMsg, setErrorMsg] = useState("");

  // ACS call state
  const [callToken, setCallToken] = useState<string>("");
  const [userId, setUserId] = useState<CommunicationUserIdentifier | null>(null);
  const [roomId, setRoomId] = useState<string>("");

  // Media controls state
  const [isMicOn, setIsMicOn] = useState(callManager.getState().isMicOn);
  const [isCameraOn, setIsCameraOn] = useState(callManager.getState().isCameraOn);
  const callViewRef = useRef<CustomCallViewHandle | null>(null);

  const hasInitialised = useRef(false);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);

  useEffect(() => {
    if (!token || hasInitialised.current) return;

    // Handle double-prefixing: if the route token is a full URL, redirect to it directly
    if (token.startsWith("http://") || token.startsWith("https://")) {
      window.location.href = token;
      return;
    }

    hasInitialised.current = true;
    setupCall(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setupCall = async (roomToken: string) => {
    try {
      // 1. Store the room token so fetchTokenData can read it
      sessionStorage.setItem("roomToken", roomToken);

      // 2. Poll until consultation status is 'ongoing' (or 'completed' within rejoin window)
      let roomData = null;
      while (true) {
        try {
          const verifyRes = await fetchTokenData();
          roomData = verifyRes?.data;
          if (!roomData?.roomId) {
            throw new Error("Invalid or expired meeting link.");
          }

          const isRejoinable = roomData.status === "completed" && !roomData.isRejoinExpired;

          if ((roomData.status === "ongoing" && roomData.startedAt) || isRejoinable) {
            break;
          } else if (roomData.status === "cancelled" || (roomData.status === "completed" && roomData.isRejoinExpired)) {
            setJoinState("ended");
            return;
          }
        } catch (pollErr: any) {
          const status = pollErr.response?.status;
          // Abort and show error screen on permanent 4xx client errors
          if (status >= 400 && status < 500) {
            throw pollErr;
          }
          if (pollErr.message === "Invalid or expired meeting link.") {
            throw pollErr;
          }
          console.warn("Temporary status verification check failed, retrying...", pollErr);
        }

        setLoadingText("Waiting for your doctor to start the call...");
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      const rid: string = roomData.roomId;
      const consultationId: string = roomData.consultationId;
      if (roomData.patientName) {
        setPatientName(roomData.patientName);
      }

      // 3. Create/Retrieve persistent ACS identity + token
      const tokenData = await getPatientUserTokenFun(consultationId, rid);
      if (!tokenData?.token || !tokenData?.user) {
        throw new Error("Failed to get call credentials.");
      }

      const acsUserId: string = tokenData.user.communicationUserId;

      // 4. Add the patient user to the ACS room as Attendee
      await addUserToRoomFun(acsUserId, rid, "Attendee" as any);

      // 5. Ready to render the call
      setCallToken(tokenData.token);
      setUserId({ communicationUserId: acsUserId });
      setRoomId(rid);
      setJoinState("ready");
      setStartedAt(roomData.startedAt);
      setAccumulatedSeconds(roomData.callDurationSeconds || 0);
    } catch (err: any) {
      console.error("[PatientJoin] setup error:", err);
      setErrorMsg(err?.response?.data?.message || err?.message || "Something went wrong. Please try the link again.");
      setJoinState("error");
    }
  };

  const handleEndCall = async () => {
    try {
      await callManager.hangUp();
    } catch (_) {}
    setJoinState("ended");
  };

  if (joinState === "loading") {
    return (
      <div className={style.center}>
        <div className={style.spinner} />
        <p className={style.loadingText}>{loadingText}</p>
      </div>
    );
  }

  if (joinState === "error") {
    return (
      <div className={style.center}>
        <span className={`material-icons-round ${style.errorIcon}`}>error_outline</span>
        <p className={style.errorText}>{errorMsg}</p>
        <p className={style.errorSub}>The meeting link may have expired. Please contact support.</p>
      </div>
    );
  }

  if (joinState === "ended") {
    return (
      <div className={style.center}>
        <span className={`material-icons-round ${style.endedIcon}`}>call_end</span>
        <p className={style.endedTitle}>Call Ended</p>
        <p className={style.endedSub}>Your consultation has ended. You may close this window.</p>
      </div>
    );
  }

  // ready — render the custom call view matching doctor UI
  return (
    <div className={style.patientBody}>
      {/* Video Area */}
      <div className={style.videoContainer}>
        {callToken && userId && roomId ? (
          <div style={{ width: "100%", height: "100%" }}>
            <CustomCallView
              ref={callViewRef}
              token={callToken}
              userId={userId.communicationUserId}
              roomId={roomId}
              displayName={patientName}
              waitingText="Waiting for doctor..."
              onCallEnded={() => {
                setJoinState("ended");
              }}
              onMicToggle={(v) => setIsMicOn(v)}
              onCameraToggle={(v) => setIsCameraOn(v)}
            />
          </div>
        ) : (
          <div className={style.videoPlaceholder}>
            <span className="material-icons-round">person</span>
            <p>Connecting to doctor...</p>
          </div>
        )}
      </div>

      {/* Top Bar */}
      <div className={style.topBar}>
        <div className={style.topBarLeft}>
          <div className={style.callInfo}>
            <span className={style.callInfoTitle}>Doctor Consultation</span>
            <span className={style.callInfoSub}>Video Call</span>
          </div>
        </div>
        <div className={style.callTimer}>
          <span className={style.callTimerDot} />
           {startedAt ? (
            <CallTimer startedAt={startedAt} accumulatedSeconds={accumulatedSeconds} />
          ) : (
            "00:00"
          )}
        </div>
      </div>

      {/* Bottom Controls Bar */}
      <div className={style.controlsBar}>
        <div className={style.controlsMain}>
          <button
            className={`${style.controlBtn} ${!isMicOn ? style.active : ""}`}
            onClick={() => callViewRef.current?.toggleMic()}
          >
            <span className="material-icons-round">{isMicOn ? "mic" : "mic_off"}</span>
          </button>
          <button
            className={`${style.controlBtn} ${!isCameraOn ? style.active : ""}`}
            onClick={() => callViewRef.current?.toggleCamera()}
          >
            <span className="material-icons-round">{isCameraOn ? "videocam" : "videocam_off"}</span>
          </button>
          {isCameraOn && (
            <button
              className={style.controlBtn}
              onClick={() => callViewRef.current?.switchCamera()}
            >
              <span className="material-icons-round">flip_camera_ios</span>
            </button>
          )}
          <button
            className={`${style.controlBtn} ${style.endCall}`}
            onClick={handleEndCall}
          >
            <span className="material-icons-round">call_end</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const CallTimer: React.FC<{ startedAt: string; accumulatedSeconds?: number }> = ({ startedAt, accumulatedSeconds = 0 }) => {
  const [time, setTime] = useState("00:00");
  useEffect(() => {
    const startTime = new Date(startedAt).getTime();
    const tick = () => {
      const sessionSecs = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const totalSecs = accumulatedSeconds + sessionSecs;
      const mins = Math.floor(totalSecs / 60).toString().padStart(2, "0");
      const s = (totalSecs % 60).toString().padStart(2, "0");
      setTime(`${mins}:${s}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt, accumulatedSeconds]);

  return <>{time}</>;
};
